"use client"

import * as React from "react"
import * as TooltipPrimitive from "@radix-ui/react-tooltip"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const tooltipContentVariants = cva(
  // Surface shared with Popover so the two primitives read as one system: light
  // `surface-default` fill, 1px `border-color-default` (base.css applies the
  // border color globally), `shadow/sm` elevation resolved through the
  // shape-token system, `text-color-primary` text. Figma tooltip node
  // 3888:23451 — which has NO arrow, unlike the popover.
  //
  // The width ceiling is a single `min()` rather than two `max-w-*` classes:
  // Tailwind resolves both to `max-width`, so one would simply lose to the other
  // in stylesheet order regardless of how they're ordered in the class string.
  //
  // 25rem (400px) is the design ceiling, taken from Figma's multi-line example
  // (`3888:23487`, 400 x 86 — exactly two lines of 18px/1.5 text plus 16px
  // padding). `calc(100vw-2rem)` is the same viewport gutter PopoverContent
  // uses, so both overlays stop at an identical margin.
  "bg-secondary text-popover-foreground border shadow-ui animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 z-50 w-fit max-w-[min(25rem,calc(100vw-2rem))] origin-(--radix-tooltip-content-transform-origin) rounded-ui leading-[1.5] tracking-[0.03em] text-balance",
  {
    variants: {
      // Two author-selected sizes, fixed at every viewport — NOT a
      // desktop/mobile pair. `default` is the larger step, matching the
      // size convention in button.tsx.
      //
      // Padding comes from the variants' own Figma variable sets, mapped onto
      // Tailwind's built-in numeric scale (custom `--spacing-*` names would
      // shadow the `--container-*` scale — see the design-tokens spec):
      //   Size=Default (3888:23440) binds only --spacing-4 → 16px all round.
      //   Size=Small (16295:126705) binds --spacing-4 and --spacing-2 → 16px
      //   horizontal, 8px vertical.
      // Cross-checks against the rendered frames: 59px tall at 18px/1.5
      // (27px line box) leaves 32px vertical = 2 x 16; 37px tall at 14px/1.5
      // (21px) leaves 16px = 2 x 8.
      size: {
        default: "p-4 text-lg", // body/lg 18px
        sm: "px-4 py-2 text-sm", // body/sm 14px
      },
    },
    defaultVariants: {
      size: "default",
    },
  }
)

function TooltipProvider({
  // 200ms open: fast enough not to feel sluggish on an indicator the user has
  // deliberately aimed at, still non-zero so a pointer crossing the icon en route
  // elsewhere doesn't fire it. Radix's own default is 700ms — this is a deliberate
  // reduction, twice narrowed after trying it on hardware. Don't "correct" it.
  delayDuration = 200,
  // Left at Radix's default so moving between neighbouring tooltips skips the
  // open delay rather than re-waiting it.
  skipDelayDuration = 300,
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Provider>) {
  return (
    <TooltipPrimitive.Provider
      data-slot="tooltip-provider"
      delayDuration={delayDuration}
      skipDelayDuration={skipDelayDuration}
      {...props}
    />
  )
}

type TooltipContextValue = {
  open: boolean
  setOpen: (open: boolean) => void
  /**
   * True between a touch `pointerdown` on the trigger and the `click` it
   * synthesises, so that click can be recognised and suppressed.
   *
   * Assigned on EVERY `pointerdown` — `= isTouch`, not `= true` — so it cannot
   * latch. A tap that never produces a click (the user scrolls away instead) would
   * otherwise leave it set and eat the next mouse click; here the next
   * `pointerdown` of any kind clears it, and a click is always preceded by one.
   *
   * This deliberately does NOT compare timestamps across the two events. That was
   * the first implementation and it failed in Chromium while passing in jsdom:
   * React falls back to `Date.now()` when a native event's `timeStamp` is falsy,
   * so the two events could be measured on different clocks — a `performance.now()`
   * value against an epoch one — making the elapsed time ~1.7e12 ms and blowing any
   * window. Never compare `timeStamp` between separately constructed events.
   */
  touchPendingRef: React.RefObject<boolean>
}

const TooltipContext = React.createContext<TooltipContextValue | null>(null)

/**
 * Tooltip root.
 *
 * Owns `open` rather than leaving it to Radix, because Radix opens on `focus`,
 * which only fires on focus *change*: once the trigger holds focus a tap can
 * never reopen the tooltip, so repeat taps go dead after the second one. Owning
 * the state and toggling from the value recorded before `pointerdown` makes
 * repeat taps deterministic.
 *
 * Everything else still flows through Radix's `onOpenChange` — hover, keyboard
 * focus, Escape, outside press, scroll dismissal, and the document-level
 * single-open-at-a-time coordination.
 */
function Tooltip({
  open: openProp,
  defaultOpen = false,
  onOpenChange,
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Root>) {
  const [uncontrolledOpen, setUncontrolledOpen] = React.useState(defaultOpen)
  const isControlled = openProp !== undefined
  const open = isControlled ? openProp : uncontrolledOpen

  const setOpen = React.useCallback(
    (next: boolean) => {
      if (!isControlled) setUncontrolledOpen(next)
      onOpenChange?.(next)
    },
    [isControlled, onOpenChange]
  )

  const touchPendingRef = React.useRef(false)
  const context = React.useMemo(
    () => ({ open, setOpen, touchPendingRef }),
    [open, setOpen]
  )

  return (
    <TooltipContext.Provider value={context}>
      <TooltipPrimitive.Root
        data-slot="tooltip"
        open={open}
        onOpenChange={setOpen}
        {...props}
      />
    </TooltipContext.Provider>
  )
}

function TooltipTrigger({
  onPointerDown,
  onClick,
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Trigger>) {
  const context = React.useContext(TooltipContext)

  /**
   * Tap-to-toggle. Branches on the event's own `pointerType`, so a device with
   * both a mouse and a touchscreen gets hover *and* tap from one component with
   * no render-time branch — and therefore no SSR/client divergence.
   *
   * `pointerdown` because it is first in the touch sequence, so we act before
   * any Radix handler rather than racing it, and because `click` is a MouseEvent
   * carrying no `pointerType`.
   *
   * `preventDefault()` is what keeps the tooltip open: Radix composes every
   * trigger handler with `composeEventHandlers`, which runs the consumer's
   * handler first and skips its own when the default has been prevented. That is
   * a documented extension point, not a patch — but it is also why a tooltip
   * trigger must be a dedicated non-actionable indicator: suppressing the
   * default would suppress a real button's or link's action too.
   */
  const handlePointerDown = (
    event: React.PointerEvent<HTMLButtonElement>
  ) => {
    onPointerDown?.(event)
    if (event.defaultPrevented || !context) return

    // Assigned unconditionally so the flag cannot latch — see the note on
    // touchPendingRef.
    const isTouch = event.pointerType === "touch"
    context.touchPendingRef.current = isTouch
    if (!isTouch) return

    const wasOpen = context.open
    event.preventDefault()
    context.setOpen(!wasOpen)
  }

  /**
   * Suppress the `click` a tap synthesises, whose Radix handler is
   * `onClick: onClose` — the thing that closed the tooltip milliseconds after
   * `focus` opened it, producing the flash. `preventDefault()` on `pointerdown`
   * does not carry over: `defaultPrevented` is per-event.
   */
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    onClick?.(event)
    if (event.defaultPrevented || !context) return
    if (!context.touchPendingRef.current) return

    context.touchPendingRef.current = false
    event.preventDefault()
  }

  return (
    <TooltipPrimitive.Trigger
      data-slot="tooltip-trigger"
      onPointerDown={handlePointerDown}
      onClick={handleClick}
      {...props}
    />
  )
}

function TooltipContent({
  className,
  // 8px of air between the tooltip and its trigger. Was 0, which left the surface
  // touching the edge of the trailing information icon — the trigger is small, so a
  // flush tooltip reads as attached to it rather than pointing at it.
  sideOffset = 8,
  // Radix defaults this to 0, which lets the tooltip sit flush against the viewport
  // edge: on a narrow screen a tooltip anchored to an icon near the right margin
  // gets shifted inward just far enough to fit and no further, so it butts against
  // the edge even when it is nowhere near full width. Matching PopoverContent's
  // 16px keeps a gutter, and keeps the two overlays stopping at the same margin.
  collisionPadding = 16,
  size,
  children,
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Content> &
  VariantProps<typeof tooltipContentVariants>) {
  return (
    <TooltipPrimitive.Portal>
      <TooltipPrimitive.Content
        data-slot="tooltip-content"
        sideOffset={sideOffset}
        collisionPadding={collisionPadding}
        className={cn(tooltipContentVariants({ size }), className)}
        {...props}
      >
        {children}
      </TooltipPrimitive.Content>
    </TooltipPrimitive.Portal>
  )
}

/**
 * Optional pointer, rendered as the LAST child of `TooltipContent`.
 *
 * Opt-in, never a default. The plain tooltip node (`3888:23451`) draws no arrow, and the surfaces
 * that do — the popover, and the `Feature Coachmark` (`21288:37290`) this was added for — are
 * anchored explanations of one specific control rather than a label following the pointer.
 *
 * Built as a rotated SQUARE straddling the edge, exactly as `popover.tsx` builds its own and for the
 * same reason: Radix's default arrow is an SVG polygon that can be filled but not cleanly bordered,
 * and this surface carries a 1px `--border-color-default` outline the pointer has to continue. Only
 * the two outward-facing edges are drawn; Radix rotates the wrapper per side, so `border-b border-r`
 * stays correct in the arrow's own coordinate space on all four.
 *
 * 11px square = 15.6px across and 7.8px tall once rotated, which is the coachmark's 16x8 pointer to
 * within a rounding error. VERIFY VISUALLY per side if a consumer anchors on left or right.
 */
function TooltipArrow({
  className,
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Arrow>) {
  return (
    <TooltipPrimitive.Arrow
      asChild
      width={16}
      height={8}
      data-slot="tooltip-arrow"
      {...props}
    >
      <span
        className={cn(
          "bg-secondary border-b border-r block size-[11px] translate-y-[calc(-50%_-_1px)] rotate-45",
          className
        )}
      />
    </TooltipPrimitive.Arrow>
  )
}

export { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider, TooltipArrow }
